import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Pusher from "pusher-js";
import {
  MessageSquare,
  Send,
  X,
  User,
  Calendar,
  Clock,
} from "lucide-react";

const ChatModal = ({ isOpen, onClose, selectedChat, pusherConfig }) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [pusherChannel, setPusherChannel] = useState(null);
  const messagesEndRef = useRef(null);

  // Helper function to get client initials
  const getClientInitials = (fullName) => {
    if (!fullName) return "C";
    const names = fullName.trim().split(" ");
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
  };

  // Load messages when modal opens and chat is selected
  useEffect(() => {
    if (isOpen && selectedChat && selectedChat.trips.length > 0) {
      loadChatMessages(selectedChat.trips[0].tripId);
      setupRealtimeChat(selectedChat.trips[0].tripId);
    }
  }, [isOpen, selectedChat, pusherConfig]);

  // Setup real-time chat with Pusher
  const setupRealtimeChat = (tripId) => {
    if (!pusherConfig || !tripId) return;

    // Disconnect previous channel
    if (pusherChannel) {
      pusherChannel.disconnect();
    }

    // Initialize Pusher with enhanced configuration
    const pusher = new Pusher(pusherConfig.key, {
      cluster: pusherConfig.cluster,
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
      disabledTransports: [],
    });

    // Subscribe to trip channel
    const channel = pusher.subscribe(`trip-${tripId}`);
    setPusherChannel(channel);

    // Listen for new messages
    channel.bind('new-message', (data) => {
      console.log('New message received in ChatModal:', data);
      
      // Ensure the new message has a proper timestamp
      const processedMessage = {
        ...data,
        timestamp: data.timestamp || new Date().toISOString()
      };
      
      // Add new message to messages array
      setMessages(prevMessages => {
        // Check if message already exists to avoid duplicates
        const exists = prevMessages.some(msg => msg.id === data.id);
        if (!exists) {
          return [...prevMessages, processedMessage];
        }
        return prevMessages;
      });

      // Show push notification if message is from client
      if (data.from === 'client') {
        showPushNotification(data);
      }
    });

    // Listen for push notifications
    channel.bind('push-notification', (data) => {
      console.log('Push notification received:', data);
      showPushNotification(data);
    });

    // Cleanup function
    return () => {
      channel.unbind_all();
      pusher.disconnect();
    };
  };

  // Show push notification
  const showPushNotification = (message) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`New message from ${selectedChat?.clientName || 'Client'}`, {
        body: message.message || message.text || 'New message received',
        icon: '/favicon.ico',
        tag: `message-${message.id || Date.now()}`,
        requireInteraction: true,
        silent: false,
        vibrate: [200, 100, 200]
      });
    }
  };

  // Cleanup Pusher connections on unmount
  useEffect(() => {
    return () => {
      if (pusherChannel) {
        pusherChannel.unbind_all();
        pusherChannel.disconnect();
      }
    };
  }, [pusherChannel]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Function to load chat messages
  const loadChatMessages = async (tripId) => {
    if (!tripId) return;
    
    setLoadingMessages(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/history/${tripId}?t=${Date.now()}`,
        {
          credentials: "include",
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const messages = data.data?.messages || [];
        
        // Ensure timestamps are properly formatted
        const processedMessages = messages.map((msg) => {
          // Handle different timestamp formats
          let timestamp = msg.timestamp;
          
          // If timestamp is an object, try to extract a valid date
          if (typeof timestamp === 'object' && timestamp !== null) {
            // Try common timestamp properties
            timestamp = timestamp.createdAt || timestamp.created_at || timestamp.date || timestamp.time;
            
            // If still an object, set to null to trigger fallback
            if (typeof timestamp === 'object' && timestamp !== null) {
              timestamp = null;
            }
          }
          
          // If no valid timestamp, use current time as fallback
          if (!timestamp || timestamp === '{}' || timestamp === '' || timestamp === 'Invalid Date') {
            console.warn("Invalid timestamp for message:", msg.id, "Using fallback");
            timestamp = new Date().toISOString();
          }
          
          // Validate the timestamp by trying to create a Date object
          try {
            const testDate = new Date(timestamp);
            if (isNaN(testDate.getTime())) {
              console.warn("Invalid timestamp format:", timestamp, "Using fallback");
              timestamp = new Date().toISOString();
            }
          } catch (error) {
            console.warn("Error validating timestamp:", timestamp, "Using fallback");
            timestamp = new Date().toISOString();
          }
          
          return {
            ...msg,
            timestamp: timestamp
          };
        });
        setMessages(processedMessages);
    } else {
        console.error("Failed to load messages:", response.status);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Handle sending message
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedChat || sendingMessage) return;

    setSendingMessage(true);
    try {
      // Use the most recent trip for this client
      const tripId = selectedChat.trips[0]?.tripId;

      if (!tripId) {
        console.error("No trip found for client");
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            tripId: tripId,
            message: messageInput,
            from: "agent",
            senderId: "agent-user",
            senderName: "Agent",
            senderType: "agent",
          }),
        }
      );

      if (response.ok) {
        setMessageInput("");
        // Refresh messages to show the new message
        if (selectedChat && selectedChat.trips.length > 0) {
          loadChatMessages(selectedChat.trips[0].tripId);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Format timestamp for date separator
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "No date";
    
    try {
      // Handle different timestamp formats
      let date;
      
      if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'object' && timestamp !== null) {
        // Try to extract a valid date from object
        const dateStr = timestamp.createdAt || timestamp.created_at || timestamp.date || timestamp.time || timestamp.timestamp;
        if (dateStr) {
          date = new Date(dateStr);
        } else {
          return "Unknown date";
        }
      } else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid timestamp in formatTimestamp:", timestamp);
        return "Unknown date";
      }
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      
      if (messageDate.getTime() === today.getTime()) {
        return "Today";
      } else if (messageDate.getTime() === yesterday.getTime()) {
        return "Yesterday";
      } else {
        // Check if it's within the last week
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        
        if (messageDate > weekAgo) {
          return date.toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
          });
        } else {
          return date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        }
      }
    } catch (error) {
      console.error("Error formatting timestamp:", error, timestamp);
      return "Unknown date";
    }
  };

  // Format message timestamp for display next to message
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    
    try {
      // Handle different timestamp formats
      let date;
      
      if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'object' && timestamp !== null) {
        // Try to extract a valid date from object
        const dateStr = timestamp.createdAt || timestamp.created_at || timestamp.date || timestamp.time || timestamp.timestamp;
        if (dateStr) {
          date = new Date(dateStr);
        } else {
          return "";
        }
      } else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid message timestamp:", timestamp);
        return "";
      }
      
      return date.toLocaleTimeString([], { 
        hour: "numeric", 
        minute: "2-digit",
        hour12: true 
      });
    } catch (error) {
      console.error("Error formatting message time:", error, timestamp);
      return "";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="fixed inset-4 z-50 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-700 rounded-xl flex items-center justify-center">
                    <span className="text-sm font-bold text-white">
                      {selectedChat?.clientName ? getClientInitials(selectedChat.clientName) : "C"}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedChat?.clientName}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {selectedChat?.clientEmail}
                    </p>
                  </div>
                </div>
                {selectedChat?.trips.length > 0 && (
                  <div className="flex gap-2 items-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Calendar size={12} className="mr-1" />
                      {selectedChat.trips[0].tripName}
                    </span>
                    {selectedChat.trips.length > 1 && (
                      <span className="text-xs text-gray-500">
                        +{selectedChat.trips.length - 1} more trip
                        {selectedChat.trips.length > 2 ? "s" : ""}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
              {loadingMessages ? (
                <div className="flex justify-center items-center h-full">
                  <div className="text-center">
                    <div className="mx-auto mb-4 w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                    <p className="text-gray-600">Loading messages...</p>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <MessageSquare
                    size={80}
                    className="mx-auto mb-6 text-gray-300"
                  />
                  <p className="mb-3 text-xl font-medium">
                    Start a conversation
                  </p>
                  <p className="text-gray-600">
                    Send a message to begin chatting with{" "}
                    <span className="font-medium">
                      {selectedChat?.clientName}
                    </span>
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => {
                    // Safely get message date
                    let messageDate = null;
                    let previousMessageDate = null;
                    
                    try {
                      const date = new Date(message.timestamp);
                      if (!isNaN(date.getTime())) {
                        // Use start of day for accurate date comparison
                        messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
                      }
                    } catch (error) {
                      console.error("Invalid message timestamp:", message.timestamp);
                    }
                    
                    // Get previous message date
                    if (index > 0) {
                      try {
                        const prevDate = new Date(messages[index - 1].timestamp);
                        if (!isNaN(prevDate.getTime())) {
                          // Use start of day for accurate date comparison
                          previousMessageDate = new Date(prevDate.getFullYear(), prevDate.getMonth(), prevDate.getDate()).getTime();
                        }
                      } catch (error) {
                        console.error("Invalid previous message timestamp:", messages[index - 1].timestamp);
                      }
                    }
                    
                    const showDateSeparator = messageDate && messageDate !== previousMessageDate;
                    
                    return (
                      <React.Fragment key={message.id || index}>
                        {showDateSeparator && (
                          <div className="relative my-6">
                            <div className="flex absolute inset-0 items-center">
                              <div className="w-full border-t border-gray-300" />
                            </div>
                            <div className="flex relative justify-center">
                              <span className="px-3 text-sm text-gray-500 bg-gray-50">
                                {formatTimestamp(message.timestamp)}
                              </span>
                            </div>
                          </div>
                        )}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex items-end gap-2 ${
                            message.from === 'agent' ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          {message.from === 'client' && (
                            <span className="mb-1 text-xs text-gray-500">
                              {formatMessageTime(message.timestamp)}
                            </span>
                          )}
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              message.from === 'agent'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-900 border border-gray-200'
                            }`}
                          >
                            <p className="text-sm">{message.message}</p>
                          </div>
                          {message.from === 'agent' && (
                            <span className="mb-1 text-xs text-gray-500">
                              {formatMessageTime(message.timestamp)}
                            </span>
                          )}
                        </motion.div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-6 bg-white border-t border-gray-200">
              <div className="flex gap-4">
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder={`Message ${selectedChat?.clientName}...`}
                  className="flex-1 px-4 py-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  disabled={sendingMessage}
                  autoFocus
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || sendingMessage}
                  className="flex gap-2 items-center px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingMessage ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChatModal;
